import React, {
  ComponentProps,
  useCallback,
  useState,
  useEffect,
  ReactNode,
  FunctionComponent,
  useContext,
} from "react";
import AppProviders from "../../../AppProviders";
import retry from "p-retry";
import { Helmet } from "react-helmet-async";
import { Security as OktaSecurity } from "@okta/okta-react";
import { User } from "UserContext";
import { setUser } from "logger";
import { useAuth } from "hooks/useAuth";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { compareLocalToOktaUser, environmentAbbreviation, useGetAccessToken } from "@coherehealth/common";
import { useAuthorized } from "../../../Authorization";
import AppLayout, { AppLayoutProps } from "../../AppLayout";
import { UserContext } from "../../../UserContext";
import { QmHomepageDisplay } from "./QmHomepageDisplay";

interface Appcues {
  identify: (id: string, properties: Record<string, string | boolean | number>) => void;
  group: (id: string, properties: Record<string, string | boolean | number>) => void;
}
declare global {
  interface Window {
    Appcues: Appcues;
  }
}

export const Security =
  process.env.REACT_APP_OKTA_ENABLED === "false"
    ? ({ oktaAuth, restoreOriginalUri, ...passthrough }: ComponentProps<typeof OktaSecurity>) => (
        <div {...passthrough} />
      )
    : OktaSecurity;

const INITIAL_USER_PROMISE = Promise.resolve(undefined);

function trackingGroupName(groups: string[]) {
  const realGroups = groups.filter((g) => g && g !== "Everyone");
  if (realGroups.length === 0) {
    return "none";
  }
  // We should never actually see more than one group on a user, nor should we see
  // no groups, but this allows for paranoia
  return realGroups.sort().join(";");
}

interface SecureRouteProps extends AppLayoutProps {
  children: ReactNode;
}

export function SecureRoute({ children, hideEnvIndicator }: SecureRouteProps) {
  const { authState, oktaAuth } = useAuth();
  const [userPromise, setUserPromise] = useState<Promise<User | undefined>>(() => INITIAL_USER_PROMISE);
  useEffect(() => {
    if (authState?.isAuthenticated) {
      if (userPromise === INITIAL_USER_PROMISE) {
        setUserPromise(() => retry(() => oktaAuth.getUser(), { retries: 5 }));
      }
    }
  }, [oktaAuth, userPromise, authState?.isAuthenticated]);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      userPromise?.then((user) => {
        compareLocalToOktaUser(user?.sub, oktaAuth);
      });
    }
  }, [authState?.isAuthenticated, userPromise, oktaAuth]);

  const appcues = window.Appcues;
  useEffect(() => {
    let active = true;
    if (authState?.isAuthenticated) {
      userPromise?.then((result: any) => {
        if (!active) {
          return;
        }
        setUser({
          id: result?.sub,
          email: result?.email,
        });

        if (!appcues) {
          return;
        }
        const accountName = trackingGroupName(result?.groups || []);
        const env = environmentAbbreviation();
        const regExp = /engineering\+cypress/;
        if (regExp.test(result?.email)) {
          console.warn("Appcues initialization skipped because Cypress bots are in use.");
          return;
        }
        appcues.identify(result?.sub, {
          email: result?.email,
          full_name: result?.name,
          given_name: result?.given_name,
          family_name: result?.family_name,
          role: result?.roles,
          opsGroup: result?.opsGroup,
        });
        appcues.group(`${accountName}-${env}`, {
          name: `${accountName}-${env}`,
        });
      });
    }
    return () => {
      active = false;
    };
  }, [authState?.isAuthenticated, userPromise, appcues]);

  const getToken = useGetAccessToken(oktaAuth);
  const getUser = useCallback(() => userPromise, [userPromise]);
  return (
    <AppProviders getAccessToken={getToken} getUser={getUser}>
      <div className="App">
        <Helmet>
          <title>Queue Management</title>
        </Helmet>
        <RequireAuth>
          <AppLayout hideEnvIndicator={hideEnvIndicator}>{children}</AppLayout>
        </RequireAuth>
      </div>
    </AppProviders>
  );
}

const RequireAuth: FunctionComponent<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { oktaAuth, authState } = useAuth();
  const isQMUser = useAuthorized("QM_USER");
  const isQMSupervisor = useAuthorized("SUPERVISOR");
  const isQMManager = useAuthorized("QM_MANAGER");

  const canUseQM = isQMUser;

  const [loading, setLoading] = useState<boolean>(true);
  const { getUser, queueView, gettingActiveQueue } = useContext(UserContext);
  useEffect(() => {
    getUser().then((user) => {
      if (user) {
        setLoading(false);
      }
    });
  }, [getUser]);
  const originalUri = toRelativeUrl(window.location.href, window.location.origin);

  if (authState && !authState.isAuthenticated) {
    if (!authState.isPending) {
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect();
    }
    return null;
  } else if (!authState) {
    return null;
  }

  let hasPermission = true;
  if (originalUri.startsWith("/supervisor")) {
    hasPermission = isQMSupervisor;
  } else if (originalUri.startsWith("/user_management")) {
    hasPermission = isQMManager;
  }

  return (
    <>
      <QmHomepageDisplay
        queueViewState={queueView}
        canUseQM={canUseQM}
        hasPermission={hasPermission}
        loading={loading}
        gettingActiveQueue={gettingActiveQueue}
        children={children}
      />
    </>
  );
};
